/**
 * UploadReceiptButton - 上传收据按钮组件
 * 
 * 用于触发上传收据操作的按钮，带有导出PDF功能（公账模式）
 */
import React from 'react';
import { useEffect } from 'react';
import { View, Text, TouchableOpacity, ActivityIndicator, StyleSheet, ViewStyle } from 'react-native';
import Icon from 'react-native-vector-icons/MaterialIcons';
import { MODULE_COLORS } from '@cubemeet/core';

export interface UploadReceiptButtonProps {
  /**
   * 上传收据回调
   */
  onUpload: () => void;
  /**
   * 导出PDF回调（可选，公账模式显示）
   */
  onExportPDF?: () => void;
  /**
   * 是否正在导出PDF
   */
  isExporting?: boolean;
  /**
   * 是否为私账模式（私账不显示导出按钮）
   */
  isPrivateMode?: boolean;
  /**
   * 翻译函数
   */
  t?: (key: string) => string;
  /**
   * 自定义样式
   */
  style?: ViewStyle;
}

const defaultT = (key: string) => {
  const translations: Record<string, string> = {
    'upload.button': '上传收据',
    'export.button': '导出PDF',
    'export.exporting': '导出中...',
  };
  return translations[key] || key;
};

const isLikelyKey = (s: any) => typeof s === 'string' && (s.indexOf('.') !== -1 || s.indexOf('expenseManagement') === 0);
const resolveT = (tFunc: ((k: string) => string) | undefined, key: string) => {
  try {
    if (!tFunc) return defaultT(key);
    let res = tFunc(key);
    if (!res || isLikelyKey(res)) {
      res = tFunc(`expenseManagement.${key}`);
      if (!res || isLikelyKey(res)) return defaultT(key);
    }
    return res;
  } catch (e) {
    return defaultT(key);
  }
};

/**
 * UploadReceiptButton 组件
 */
const UploadReceiptButton: React.FC<UploadReceiptButtonProps> = ({
  onUpload,
  onExportPDF,
  isExporting = false,
  isPrivateMode = false,
  t = defaultT,
  style,
}) => {
  useEffect(() => {
  }, [onUpload, isPrivateMode]);
  return (
    <View style={[styles.container, style]}>
      {/* 导出PDF按钮（仅公账） */}
      {!isPrivateMode && onExportPDF && (
        <TouchableOpacity
          style={[styles.button, styles.exportButton, isExporting && styles.buttonDisabled]}
          onPress={onExportPDF}
          disabled={isExporting}
          activeOpacity={0.8}
          accessibilityLabel={isExporting ? resolveT(t, 'export.exporting') : resolveT(t, 'export.button')}
        >
          {isExporting ? (
            <ActivityIndicator size="small" color="#666" />
          ) : (
            <Icon name={("file-download" as string).replace(/-/g, '_')} size={18} color="#666" />
          )}
        </TouchableOpacity>
      )}

      {/* 上传收据按钮 */}
      <TouchableOpacity
        style={[styles.button, styles.uploadButton]}
        onPress={onUpload}
        activeOpacity={0.8}
        accessibilityLabel={resolveT(t, 'upload.button')}
      >
        <Icon name={("cloud-upload" as string).replace(/-/g, '_')} size={18} color="#fff" />
      </TouchableOpacity>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  button: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 10,
    paddingVertical: 8,
    borderRadius: 20,
    justifyContent: 'center',
  },
  exportButton: {
    backgroundColor: '#f8f9fa',
    borderWidth: 1,
    borderColor: '#e5e7eb',
  },
  uploadButton: {
    backgroundColor: MODULE_COLORS.travel,
    borderWidth: 0,
  },
  buttonDisabled: {
    opacity: 0.5,
  },
  exportButtonText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#666',
    marginLeft: 6,
  },
  uploadButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#fff',
    marginLeft: 6,
  },
  iconWrapper: {
    // 移除圆形背景，直接显示图标
  },
  iconWrapperPrimary: {
    // 移除圆形背景，直接显示图标
  },
});

export default UploadReceiptButton;
